import config from '../../config';
import { Api, ApiConfig } from '../api';

export interface CloudAuditConnection {
  alias?: string;
  platform?: string;
  platformLabel?: string;
  displayName?: string;
  resolved: boolean;
}

export interface CloudAuditEvent {
  id: string;
  runId: string;
  seq: number;
  eventAt: string;
  phase: string;
  status: string;
  statusLabel: string;
  source?: string;
  level?: string;
  toolName?: string;
  integration?: string;
  platformLabel?: string;
  operationKind?: string;
  operationLabel?: string;
  resourceKind?: string;
  resourceLabel?: string;
  targetKind?: string;
  sideEffect?: string;
  connection?: CloudAuditConnection;
  changedFields: string[];
  changeKind?: string;
  changeCount?: number;
  sourceKinds: string[];
  auditSurface?: string;
  auditCoverage?: string;
  auditEvidenceLevel?: string;
  contentPayloadState?: string;
  contentPayloadKinds: string[];
  summaryText: string;
}

export interface CloudAuditListResponse {
  hasMore?: boolean;
  workspaceId: string;
  limit: number;
  nextCursor?: string | null;
  events: CloudAuditEvent[];
}

export interface CloudAuditApplication {
  platform?: string;
  platformLabel?: string;
  readCount: number;
  writeCount: number;
}

export interface CloudAuditActor {
  displayName?: string;
}

export interface CloudAuditAgent {
  label?: string;
}

export interface CloudAuditTimelineEvent {
  id: string;
  eventAt: string;
  status: string;
  statusLabel: string;
  platformLabel?: string;
  operationKind?: string;
  operationLabel?: string;
  resourceKind?: string;
  resourceLabel?: string;
  actionLabel: string;
  description?: string;
  applicationAccess?: 'read' | 'write';
  connection?: CloudAuditConnection;
  changedFields: string[];
  contentPayloadState?: string;
  contentPayloadKinds: string[];
}

export interface CloudAuditRun {
  runId: string;
  title: string;
  sourceTask?: string;
  status: string;
  statusLabel: string;
  actor?: CloudAuditActor;
  agent?: CloudAuditAgent;
  startedAt?: string;
  endedAt?: string;
  lastEventAt?: string;
  eventCount: number;
  businessEventCount: number;
  readCount: number;
  writeCount: number;
  conversationContentState?: string;
  applications: CloudAuditApplication[];
  timeline: CloudAuditTimelineEvent[];
}

export interface CloudAuditRunListResponse {
  hasMore?: boolean;
  workspaceId: string;
  limit: number;
  nextCursor?: string | null;
  runs: CloudAuditRun[];
}

export const hasCloudAuditRunPaginationMetadata = (
  response: Pick<CloudAuditRunListResponse, 'hasMore' | 'nextCursor'>,
): boolean => (
  typeof response.hasMore === 'boolean'
  || typeof response.nextCursor === 'string'
);

export interface CloudAuditListOptions {
  cursor?: string;
  endTime?: string;
  limit?: number;
  startTime?: string;
}

export type CloudAuditContentKind = 'before_after' | 'raw_diff' | 'content_snapshot';

export interface CloudAuditContentResponse {
  eventId: string;
  workspaceId: string;
  runId: string;
  seq: number;
  contentKind: CloudAuditContentKind;
  contentSchemaVersion: number;
  payload: unknown;
  payloadHash: string;
  capturedAt: string;
  accessAuditId: string;
}

export interface CloudAuditRunConversationMessage {
  messageId?: string;
  role?: string;
  source?: string;
  messageAt?: string;
  contentText?: string;
}

export interface CloudAuditRunConversationResponse {
  workspaceId: string;
  runId: string;
  sourceTask?: string;
  actor?: CloudAuditActor;
  firstMessageAt?: string;
  lastMessageAt?: string;
  messageCount: number;
  accessAuditId: string;
  messages: CloudAuditRunConversationMessage[];
}

const workspaceHeaders = (workspaceId: string) => ({
  headers: { 'X-Workspace-Id': workspaceId },
});

const eventCollectionPath = (workspaceId: string) => (
  `/api/workspaces/${encodeURIComponent(workspaceId)}/agent-run-ledger/events`
);

const runCollectionPath = (workspaceId: string) => (
  `/api/workspaces/${encodeURIComponent(workspaceId)}/agent-run-ledger/runs`
);

const auditListParams = (options: number | CloudAuditListOptions) => {
  const normalizedOptions = typeof options === 'number' ? { limit: options } : options;

  return {
    limit: normalizedOptions.limit ?? 100,
    ...(normalizedOptions.cursor ? { cursor: normalizedOptions.cursor } : {}),
    ...(normalizedOptions.startTime ? { start_time: normalizedOptions.startTime } : {}),
    ...(normalizedOptions.endTime ? { end_time: normalizedOptions.endTime } : {}),
  };
};

/** Account-server-only client for workspace cloud audit records. */
export class AgentRunLedgerService {
  private api: Api;

  constructor(apiConfig: ApiConfig) {
    this.api = new Api(apiConfig);
  }

  async listEvents(
    workspaceId: string,
    options: number | CloudAuditListOptions = 100,
  ): Promise<CloudAuditListResponse> {
    return this.api.get<CloudAuditListResponse>(eventCollectionPath(workspaceId), {
      params: auditListParams(options),
      ...workspaceHeaders(workspaceId),
    });
  }

  async listRuns(
    workspaceId: string,
    options: number | CloudAuditListOptions = 100,
  ): Promise<CloudAuditRunListResponse> {
    return this.api.get<CloudAuditRunListResponse>(runCollectionPath(workspaceId), {
      params: auditListParams(options),
      ...workspaceHeaders(workspaceId),
    });
  }

  async getEventContent(
    workspaceId: string,
    eventId: string,
  ): Promise<CloudAuditContentResponse> {
    return this.api.get<CloudAuditContentResponse>(
      `${eventCollectionPath(workspaceId)}/${encodeURIComponent(eventId)}/content`,
      workspaceHeaders(workspaceId),
    );
  }

  async getRunConversation(
    workspaceId: string,
    runId: string,
  ): Promise<CloudAuditRunConversationResponse> {
    return this.api.get<CloudAuditRunConversationResponse>(
      `${runCollectionPath(workspaceId)}/${encodeURIComponent(runId)}/conversation`,
      workspaceHeaders(workspaceId),
    );
  }
}

export const agentRunLedgerService = new AgentRunLedgerService({
  baseURL: config.services.auth.apiHost,
  headers: { 'Content-Type': 'application/json' },
});
